/**
 * Utility functions to calculate position profit.
 *
 * These provide client-side fallback calculations when backend-computed
 * profit fields (net_profit_usdt, gross_profit_usdt, etc.) are null,
 * except for tracking-ended positions, whose final profit is unknown.
 *
 * NOTE: This client-side fallback exists because old execution positions
 * (from early testing-in-prod phase) don't have these values populated in the DB.
 * Backtest positions have them calculated by the backend.
 *
 * TODO: Once we no longer care about these old ended executions, we can simplify
 * this to just use backend data directly (position.net_profit_usdt, etc.) and
 * remove the client-side calculation fallback.
 *
 * Currency detection: USD pairs have gross_profit_usdt populated by the backend.
 * Non-USD pairs (e.g. EURPLN) have profit in net_profit_quote instead.
 * We use field presence (not value) to detect which currency path to use,
 * so breakeven trades ($0 profit) are handled correctly in both cases.
 */

import type { TDT } from "@src/types";
import { isPositionTrackingEnded } from "@src/utils/positionStatus";

export interface PositionProfitResult {
  grossProfit: number;
  netProfit: number;
  profitPct: number;
  totalFees: number;
}

/**
 * Determine if the backend computed USD-denominated profit for this position.
 * USD pairs have gross_profit_usdt populated. Non-USD pairs (forex) don't.
 */
function hasUsdProfit(position: TDT.ApiPosition): boolean {
  return position.gross_profit_usdt != null && position.net_profit_usdt != null;
}

/**
 * Determine if the backend computed quote-denominated profit for this position.
 * Non-USD pairs (e.g. EURPLN) have net_profit_quote populated.
 */
function hasQuoteProfit(position: TDT.ApiPosition): boolean {
  return position.net_profit_quote != null;
}

/**
 * Calculate position profit from available position data.
 *
 * Uses backend-computed values if available, otherwise calculates from raw data.
 * Only works for closed positions (with exit_price).
 *
 * @param position - The position to calculate profit for
 * @returns Profit values, or null if position is not closed
 */
export function calculatePositionProfit(position: TDT.ApiPosition): PositionProfitResult | null {
  if (isPositionTrackingEnded(position)) return null;

  // Skip open positions - no exit price means we can't calculate
  if (!position.end_ts || position.exit_price == null) {
    return null;
  }

  // USD pairs: backend computed gross/net in USD
  if (hasUsdProfit(position)) {
    const totalFees = position.total_fees_usdt ?? 0;
    const profitPct = position.net_profit_pct ?? 0;

    return {
      grossProfit: position.gross_profit_usdt!,
      netProfit: position.net_profit_usdt!,
      profitPct,
      totalFees,
    };
  }
  // Non-USD pairs (e.g. EURPLN): profit is in quote currency
  // Use quote-denominated fees to avoid mixing currencies
  // gross = net + fees + funding (inverse of backend: net = gross - fees - funding)
  if (hasQuoteProfit(position)) {
    const totalFees = position.total_fees_quote ?? 0;
    const totalFunding = position.total_funding_fee ?? 0;
    const profitPct = position.net_profit_pct ?? 0;

    return {
      grossProfit: position.net_profit_quote! + totalFees + totalFunding,
      netProfit: position.net_profit_quote!,
      profitPct,
      totalFees,
    };
  }

  // Calculate from raw data (execution positions need this)
  const entryPrice = position.entry_price;
  const exitPrice = position.exit_price;
  const entryAmount = position.entry_amount;
  const direction = position.direction_name;
  const totalFees = position.total_fees_usdt ?? 0;
  const collateralIn = position.collateral_in ?? 0;

  // Validate we have the required data
  if (entryPrice == null || exitPrice == null || entryAmount == null || !direction) {
    return null;
  }

  // Calculate gross profit based on direction
  // Long: profit when exit > entry
  // Short: profit when entry > exit
  let grossProfit: number;
  if (direction === "long") {
    grossProfit = (exitPrice - entryPrice) * entryAmount;
  } else {
    grossProfit = (entryPrice - exitPrice) * entryAmount;
  }

  // Net profit = gross - fees
  const netProfit = grossProfit - totalFees;

  // Profit percentage relative to collateral invested
  // Fallback: if no collateral_in, estimate from notional / leverage
  let effectiveCollateral = collateralIn;
  if (effectiveCollateral == null || effectiveCollateral <= 0) {
    const notional = entryPrice * entryAmount;
    const leverage = position.leverage ?? 1;
    effectiveCollateral = notional / leverage;
  }
  const profitPct = effectiveCollateral > 0 ? (netProfit / effectiveCollateral) * 100 : 0;

  return {
    grossProfit,
    netProfit,
    profitPct,
    totalFees,
  };
}

/**
 * Get the net profit for a position, calculating if necessary.
 *
 * This is the primary function to use when you need profit for display/calculations.
 * Returns 0 for open positions.
 */
export function getPositionNetProfit(position: TDT.ApiPosition): number | null {
  if (isPositionTrackingEnded(position)) return null;

  // Skip open positions
  if (!position.end_ts) {
    return 0;
  }

  // Prefer USD, fall back to quote for non-USD pairs
  if (hasUsdProfit(position)) {
    return position.net_profit_usdt!;
  }
  if (hasQuoteProfit(position)) {
    return position.net_profit_quote!;
  }

  // Calculate from raw data
  const result = calculatePositionProfit(position);
  return result?.netProfit ?? 0;
}

/**
 * Get the gross profit for a position, calculating if necessary.
 */
export function getPositionGrossProfit(position: TDT.ApiPosition): number | null {
  if (isPositionTrackingEnded(position)) return null;

  // Skip open positions
  if (!position.end_ts) {
    return 0;
  }

  // Prefer USD
  if (hasUsdProfit(position)) {
    return position.gross_profit_usdt!;
  }
  // Non-USD pairs: compute gross from quote profit + fees + funding (same currency)
  if (hasQuoteProfit(position)) {
    return (
      position.net_profit_quote! +
      (position.total_fees_quote ?? 0) +
      (position.total_funding_fee ?? 0)
    );
  }

  // Calculate from raw data
  const result = calculatePositionProfit(position);
  return result?.grossProfit ?? 0;
}

/**
 * Get the profit percentage for a position, calculating if necessary.
 */
export function getPositionProfitPct(position: TDT.ApiPosition): number | null {
  if (isPositionTrackingEnded(position)) return null;

  // Skip open positions
  if (!position.end_ts) {
    return 0;
  }

  // Use backend-stored percentage (always available for closed positions)
  if (position.net_profit_pct != null) {
    return position.net_profit_pct;
  }

  // Fallback for very old data (should rarely happen)
  const result = calculatePositionProfit(position);
  return result?.profitPct ?? 0;
}

/**
 * Check if a position is a winning trade.
 */
export function isWinningPosition(position: TDT.ApiPosition): boolean {
  const profit = getPositionNetProfit(position);
  return profit != null && profit > 0;
}

/**
 * Check if a position is a losing trade.
 */
export function isLosingPosition(position: TDT.ApiPosition): boolean {
  const profit = getPositionNetProfit(position);
  // Only count as loss if actually closed with negative profit
  return position.end_ts != null && profit != null && profit < 0;
}
